import jwtDecode from 'jwt-decode';
import jsonSoftParse from '@zenbusiness/application-commons-utils/jsonSoftParse';
import isNotUndefinedOrNull from '@zenbusiness/application-commons-utils/isNotUndefinedOrNull';
import memoize from 'lodash/memoize.js';
import { LS_REDIRECT_TO, LS_LOGIN_STATE, LS_ID_TOKEN, LS_ACCESS_TOKEN, LS_EMAIL, LS_ACCOUNT_UUID, LS_OBO, LS_USER_PROFILE, LS_CREATION_DATE, LS_FIRST_NAME, LS_LAST_NAME, LS_WEBVIEW_DATA, PROFILE_PAYLOAD_KEY } from "./constants.mjs";
/**
 * @typedef UserProfile
 * @property {string} [accountUuid]
 * @property {string} [userUuid]
 * @property {string} [email]
 * @property {string} [name]
 * @property {string} [firstName]
 * @property {string} [lastName]
 * @property {string} [createdAt]
 */
/**
 * @typedef AuthenticationState
 * @property {'customer'|'employee'|'unknown'} [type]
 * @property {number} [expiresAt]
 * @property {string} [idToken]
 * @property {string} [accessToken]
 * @property {string} [username]
 * @property {string} [accountUuid]
 * @property {string} [userUuid]
 * @property {boolean} authenticated
 * @property {string} [obo]
 * @property {UserProfile} user
 * @property {string} [redirectTo]
 */
/** @typedef {import('jwt-decode').JwtPayload} JwtPayload */
/**
 * @typedef {JwtPayload & {
 *   "https://www.zenbusiness.com/email": string,
 *   "https://www.zenbusiness.com/account_uuid": string,
 *   "https://www.zenbusiness.com/user_uuid": string
 * }} AccessTokenPayload
 */
/**
 * @typedef {JwtPayload & {
 *   nickname: string,
 *   picture: string,
 *   updated_at: string
 *   email: string,
 * }} BaseIdTokenPayload
 */
/**
 * @typedef {BaseIdTokenPayload & {
 *   "https://www.zenbusiness.com/profile": {
 *     accountUuid: string,
 *     userUuid: string,
 *     firstName: string,
 *     lastName: string,
 *     createdAt: string,
 *     name: string
 *   }
 * }} CustomerIdTokenPayload
 */
/**
 * @typedef {BaseIdTokenPayload & {
 *   given_name: string,
 *   family_name: string
 * }} EmployeeIdTokenPayload
 */
/**
 * @typedef {BaseIdTokenPayload | CustomerIdTokenPayload | EmployeeIdTokenPayload} IdTokenPayload
 */
/**
 * JWT parsing under a one second memoization (by token). Ensures
 * that multiple calls to decode the same token don't invoke
 * decoding over and over again.
 * @type {(string) => JwtPayload}
 */
const memoizedDecode = memoize(jwtDecode);
export default class AuthStorage {
  static #store = null;

  /**
   * Returns the WebStorage being used for authentication.
   * This defaults to sessionStorage, but will use localStorage if an
   * idToken is already present.
   *
   * @returns {Storage}
   */
  static get store() {
    if (!AuthStorage.#store) {
      const sessionHas = window.sessionStorage.getItem(LS_LOGIN_STATE) || window.sessionStorage.getItem(LS_ID_TOKEN);
      const localHas = window.localStorage.getItem(LS_LOGIN_STATE) || window.localStorage.getItem(LS_ID_TOKEN);

      // if sessionStorage has any login data, use that from now on
      if (sessionHas) {
        AuthStorage.#store = window.sessionStorage;

        // if localStorage has any login data, assume we're still using
        // that and stick to it for this page load
      } else if (localHas) {
        AuthStorage.#store = window.localStorage;
      } else {
        // if neither of them have an active login, just prefer sessionStorage
        return window.sessionStorage;
      }
    }
    return AuthStorage.#store;
  }

  /**
   * Resets which storage method it uses. This is an internal api,
   * exposed purely for testing.
   *
   * @protected
   */
  static $reset() {
    this.#store = null;
  }

  /**
   * Is the current token expired.
   *
   * @returns {boolean}
   */
  static get isExpired() {
    return Date.now() >= AuthStorage.expiresAt;
  }

  /**
   * Is the an idToken present and current
   *
   * @returns {boolean}
   * @alias authenticated
   */
  static get isAuthenticated() {
    return !!AuthStorage.idToken && !AuthStorage.isExpired;
  }

  /**
   * Returns the token expiration as an epoch timestamp.
   *
   * @returns {number}
   */
  static get expiresAt() {
    const {
      idToken
    } = AuthStorage;
    if (idToken) {
      /** @type {JwtPayload} */
      const jwt = jwtDecode(idToken);
      return jwt.exp * 1000;
    }
    return 0;
  }

  /**
   * Returns the token expiration as a JS Date
   *
   * @returns {Date}
   */
  static get expireDate() {
    const {
      expiresAt
    } = AuthStorage;
    const date = new Date(expiresAt);
    if (!expiresAt || Number.isNaN(date.valueOf())) return null;
    return date;
  }

  /**
   * Returns the token issue time as an epoch timestamp
   *
   * @returns {number}
   */
  static get issuedAt() {
    const {
      idToken
    } = AuthStorage;
    if (idToken) {
      /** @type {IdTokenPayload} */
      const jwt = jwtDecode(idToken);
      return jwt.iat * 1000;
    }
    return 0;
  }

  /**
   * Returns the token issue time as a JS Date
   *
   * @returns {Date}
   */
  static get issueDate() {
    const {
      issuedAt
    } = AuthStorage;
    const date = new Date(issuedAt);
    if (!issuedAt || Number.isNaN(date.valueOf())) return null;
    return date;
  }

  /**
   * Returns the current idToken in local/session storage, decoded
   *
   * @returns {IdTokenPayload}
   */
  static get idTokenDecoded() {
    const {
      idToken
    } = AuthStorage;
    if (idToken) {
      try {
        const jwt = /** @type {IdTokenPayload} */memoizedDecode(idToken);
        return jwt;
      } catch (e) {
        console.error(e, {
          idToken
        });
        return (/** @type {IdTokenPayload} */{}
        );
      }
    }
    return (/** @type {IdTokenPayload} */{}
    );
  }

  /**
   * Returns the current idToken in local/session storage
   *
   * @returns {string}
   */
  static get idToken() {
    return AuthStorage.store.getItem(LS_ID_TOKEN);
  }

  /**
   * Sets the users current idToken in local/session storage,
   * or deletes it if no token is provided.
   *
   * @param {string | null} [token]
   * @returns {void}
   */
  static set idToken(token) {
    if (token === undefined || token === null) {
      window.localStorage.removeItem(LS_ID_TOKEN);
      window.sessionStorage.removeItem(LS_ID_TOKEN);
    } else {
      window.localStorage.setItem(LS_ID_TOKEN, token);
      window.sessionStorage.setItem(LS_ID_TOKEN, token);
    }
  }

  /**
   * Returns the current accessToken in local/session storage, decoded.
   *
   * @returns {AccessTokenPayload}
   */
  static get accessTokenDecoded() {
    const {
      accessToken
    } = AuthStorage;
    if (accessToken) {
      try {
        const jwt = /** @type {AccessTokenPayload} */memoizedDecode(accessToken);
        return jwt;
      } catch (e) {
        console.error(e, {
          accessToken
        });
        return (/** @type {AccessTokenPayload} */{}
        );
      }
    }
    return (/** @type {AccessTokenPayload} */{}
    );
  }

  /**
   * Returns the current accessToken in local/session storage
   *
   * @returns {string}
   */
  static get accessToken() {
    return AuthStorage.store.getItem(LS_ACCESS_TOKEN);
  }

  /**
   * Sets the users current accessToken in local/session storage,
   * or deletes it if no token is provided.
   *
   * @param {string | null} [token]
   * @returns {void}
   */
  static set accessToken(token) {
    if (token === undefined || token === null) {
      window.localStorage.removeItem(LS_ACCESS_TOKEN);
      window.sessionStorage.removeItem(LS_ACCESS_TOKEN);
    } else {
      window.localStorage.setItem(LS_ACCESS_TOKEN, token);
      window.sessionStorage.setItem(LS_ACCESS_TOKEN, token);
    }
  }

  /**
   * Returns the kind of user based on their idToken.
   * @returns {'customer'|'employee'|'unknown'}
   */
  static get type() {
    const {
      sub
    } = AuthStorage.idTokenDecoded;
    if (!sub) return undefined;
    if (sub.startsWith('samlp|OktaZenGarden')) {
      return 'employee';
    }
    if (sub.startsWith('auth0|')) {
      return 'customer';
    }
    return 'unknown';
  }

  /**
   * Returns the current user's email in local/session storage
   *
   * @returns {string}
   */
  static get username() {
    return AuthStorage.store.getItem(LS_EMAIL);
  }

  /**
   * Sets the users current user's email in local/session storage,
   * or deletes it if no value is provided.
   *
   * @param {string | null} [email]
   * @returns {void}
   */
  static set username(email) {
    if (email === undefined || email === null) {
      window.localStorage.removeItem(LS_EMAIL);
      window.sessionStorage.removeItem(LS_EMAIL);
    } else {
      window.localStorage.setItem(LS_EMAIL, email);
      window.sessionStorage.setItem(LS_EMAIL, email);
    }
  }

  /**
   * Returns the current user's account UUID in local/session storage
   *
   * @returns {string}
   */
  static get accountUuid() {
    return AuthStorage.store.getItem(LS_ACCOUNT_UUID);
  }

  /**
   * Sets the current user's account UUID in local/session storage,
   * or deletes it if no token is provided.
   *
   * @param {string | null} [uuid]
   * @returns {void}
   */
  static set accountUuid(uuid) {
    if (uuid === undefined || uuid === null) {
      window.localStorage.removeItem(LS_ACCOUNT_UUID);
      window.sessionStorage.removeItem(LS_ACCOUNT_UUID);
    } else {
      window.localStorage.setItem(LS_ACCOUNT_UUID, uuid);
      window.sessionStorage.setItem(LS_ACCOUNT_UUID, uuid);
    }
  }

  /**
   * Returns the current user's account UUID in local/session storage
   *
   * @returns {string}
   */
  static get userUuid() {
    return AuthStorage.accessTokenDecoded['https://www.zenbusiness.com/user_uuid'];
  }

  /**
   * Returns true if an idToken present and current
   *
   * @returns {boolean}
   * @alias isAuthenticated
   */
  static get authenticated() {
    return AuthStorage.isAuthenticated;
  }

  /**
   * Returns the current On Behalf Of email in local/session storage,
   * if one is defined.
   *
   * @returns {string}
   */
  static get obo() {
    return AuthStorage.store.getItem(LS_OBO);
  }

  /**
   * Sets the current On Behalf Of email in local/session storage,
   * or deletes it if set to null.
   *
   * @param {string | null} email
   * @returns {void}
   */
  static set obo(email) {
    if (email === undefined || email === null) {
      window.localStorage.removeItem(LS_OBO);
      window.sessionStorage.removeItem(LS_OBO);
    } else {
      window.localStorage.setItem(LS_OBO, email);
      window.sessionStorage.setItem(LS_OBO, email);
    }
  }

  /**
   * Returns the url to redirect to after successful login.
   *
   * @returns {string}
   */
  static get redirectTo() {
    return AuthStorage.store.getItem(LS_REDIRECT_TO);
  }

  /**
   * Sets the url to redirect to after successful login,
   * or deletes it if set to null.
   *
   * @param {string | null} url
   * @returns {void}
   */
  static set redirectTo(url) {
    if (url === undefined || url === null) {
      window.localStorage.removeItem(LS_REDIRECT_TO);
      window.sessionStorage.removeItem(LS_REDIRECT_TO);
    } else {
      window.localStorage.setItem(LS_REDIRECT_TO, url);
      window.sessionStorage.setItem(LS_REDIRECT_TO, url);
    }
  }

  /**
   * Returns the auth0 login nonce stored prior to initiating login
   *
   * @returns {string}
   */
  static get loginState() {
    return AuthStorage.store.getItem(LS_LOGIN_STATE);
  }

  /**
   * Sets the auth0 login nonce stored prior to initiating login,
   * or deletes it if set to null.
   *
   * @param {string | null} nonce
   * @returns {void}
   */
  static set loginState(nonce) {
    if (nonce === undefined || nonce === null) {
      window.localStorage.removeItem(LS_LOGIN_STATE);
      window.sessionStorage.removeItem(LS_LOGIN_STATE);
    } else {
      window.localStorage.setItem(LS_LOGIN_STATE, nonce);
      window.sessionStorage.setItem(LS_LOGIN_STATE, nonce);
    }
  }

  /**
   * Returns the user's first name in storage
   *
   * @deprecated This value should now be taken from the
   * user profile object
   * @returns {string}
   */
  static get firstName() {
    return AuthStorage.store.getItem(LS_FIRST_NAME);
  }

  /**
   * Sets the user's first name in storage,
   * or deletes it if set to null.
   *
   * @deprecated This value is only updated for legacy purposes
   * @param {string | null} [value]
   * @returns {void}
   */
  static set firstName(value) {
    if (value === undefined || value === null) {
      window.localStorage.removeItem(LS_FIRST_NAME);
      window.sessionStorage.removeItem(LS_FIRST_NAME);
    } else {
      window.localStorage.setItem(LS_FIRST_NAME, value);
      window.sessionStorage.setItem(LS_FIRST_NAME, value);
    }
  }

  /**
   * Returns the user's last name in storage
   *
   * @deprecated This value should now be taken from the
   * user profile object
   * @returns {string}
   */
  static get lastName() {
    return AuthStorage.store.getItem(LS_LAST_NAME);
  }

  /**
   * Sets the user's last name in storage,
   * or deletes it if set to null.
   *
   * @deprecated This value is only updated for legacy purposes
   * @param {string | null} [value]
   * @returns {void}
   */
  static set lastName(value) {
    if (value === undefined || value === null) {
      window.localStorage.removeItem(LS_LAST_NAME);
      window.sessionStorage.removeItem(LS_LAST_NAME);
    } else {
      window.localStorage.setItem(LS_LAST_NAME, value);
      window.sessionStorage.setItem(LS_LAST_NAME, value);
    }
  }

  /**
   * Returns the the user's account creation datetime in storage as an ISO-8601
   * formatted string.
   *
   * @deprecated This value should now be taken from the
   * user profile object
   * @returns {string}
   */
  static get creationDate() {
    return AuthStorage.store.getItem(LS_CREATION_DATE);
  }

  /**
   * Sets the the user's account creation datetime in storage as an ISO-8601
   * formatted string, or deletes it if set to null.
   *
   * @deprecated This value is only updated for legacy purposes
   * @param {string | null} [value]
   * @returns {void}
   */
  static set creationDate(value) {
    if (value === undefined || value === null) {
      window.localStorage.removeItem(LS_CREATION_DATE);
      window.sessionStorage.removeItem(LS_CREATION_DATE);
    } else {
      window.localStorage.setItem(LS_CREATION_DATE, value);
      window.sessionStorage.setItem(LS_CREATION_DATE, value);
    }
  }

  /**
   * Generates the `user` object returned by the authentication context, based
   * on profile data received from auth0 and cached in local/session
   *
   * @returns {{
   *   accountUuid: string,
   *   accountEmail: string
   *   userUuid: string,
   *   email: string,
   *   name: string,
   *   firstName: string,
   *   lastName: string,
   *   createdAt: string
   * }}
   */
  static get user() {
    if (!this.idToken) return undefined;
    const profile = jsonSoftParse(AuthStorage.store.getItem(LS_USER_PROFILE)) || {};
    const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ');
    if (name) profile.name = name;
    const raw = {
      accountUuid: this.accountUuid,
      ...profile,
      email: this.username,
      accountEmail: this.obo || this.username
    };
    return Object.freeze(raw); // make it unmutable
  }

  /**
   * Sets the user profile cache in local/session storage.
   *
   * @param {{
   *  userUuid: string,
   *  email: string,
   *  name: string,
   *  firstName: string,
   *  lastName: string,
   *  createdAt: string
   * }} input
   */
  static set user(input) {
    if (input === undefined || input === null) {
      window.localStorage.removeItem(LS_USER_PROFILE);
      window.sessionStorage.removeItem(LS_USER_PROFILE);
    } else {
      window.localStorage.setItem(LS_USER_PROFILE, JSON.stringify(input));
      window.sessionStorage.setItem(LS_USER_PROFILE, JSON.stringify(input));
    }
  }
  static initializeWebview() {
    const {
      idToken,
      accessToken
    } = jsonSoftParse(window.sessionStorage.getItem(LS_WEBVIEW_DATA)) || {};
    if (idToken && accessToken) {
      /** @type {CustomerIdTokenPayload} */
      const payload = jwtDecode(idToken);
      window.sessionStorage.setItem(LS_WEBVIEW_DATA, '{}');
      AuthStorage.processAuthSuccess({
        idTokenPayload: payload,
        idToken,
        accessToken
      });
    }
  }

  // eslint-disable-next-line jsdoc/require-returns
  /**
   * Generates the full auth context as cached in local/session storage
   *
   * @param {{
   *  idToken: string,
   *  accessToken: string,
   *  idTokenPayload: {
   *   email: string,
   *   'https://www.zenbusiness.com/profile': object
   *  }
   * }} auth
   * @returns {import('./auth-storage').AuthenticationState}
   */
  static processAuthSuccess(auth) {
    const {
      idToken,
      accessToken,
      idTokenPayload: {
        email: username,
        [PROFILE_PAYLOAD_KEY]: user = {}
      } = {}
    } = auth;
    const {
      redirectTo
    } = AuthStorage;
    AuthStorage.idToken = idToken;
    AuthStorage.accessToken = accessToken;
    AuthStorage.accountUuid = user.accountUuid;
    AuthStorage.username = username;
    AuthStorage.user = user;

    // these are legacy values, consumers should use the user value
    AuthStorage.firstName = user.firstName;
    AuthStorage.lastName = user.lastName;
    AuthStorage.creationDate = user.createdAt;

    // Clean up storage
    AuthStorage.loginState = null;
    // We need the redirectTo property so consumers can perform a redirect
    const returnAuthStorage = AuthStorage.toJSON();

    // Clean Up Redirect
    if (isNotUndefinedOrNull(redirectTo)) {
      AuthStorage.redirectTo = null;
    }
    return returnAuthStorage;
  }

  /**
   * Purges all auth values from local and session storage
   *
   * @returns {void}
   */
  static clean() {
    window.localStorage.removeItem(LS_REDIRECT_TO);
    window.localStorage.removeItem(LS_LOGIN_STATE);
    window.localStorage.removeItem(LS_ID_TOKEN);
    window.localStorage.removeItem(LS_ACCESS_TOKEN);
    window.localStorage.removeItem(LS_EMAIL);
    window.localStorage.removeItem(LS_ACCOUNT_UUID);
    window.localStorage.removeItem(LS_OBO);
    window.localStorage.removeItem(LS_FIRST_NAME);
    window.localStorage.removeItem(LS_LAST_NAME);
    window.localStorage.removeItem(LS_CREATION_DATE);
    window.localStorage.removeItem(LS_USER_PROFILE);
    window.sessionStorage.removeItem(LS_REDIRECT_TO);
    window.sessionStorage.removeItem(LS_LOGIN_STATE);
    window.sessionStorage.removeItem(LS_ID_TOKEN);
    window.sessionStorage.removeItem(LS_ACCESS_TOKEN);
    window.sessionStorage.removeItem(LS_EMAIL);
    window.sessionStorage.removeItem(LS_ACCOUNT_UUID);
    window.sessionStorage.removeItem(LS_OBO);
    window.sessionStorage.removeItem(LS_FIRST_NAME);
    window.sessionStorage.removeItem(LS_LAST_NAME);
    window.sessionStorage.removeItem(LS_CREATION_DATE);
    window.sessionStorage.removeItem(LS_USER_PROFILE);
  }

  // eslint-disable-next-line jsdoc/require-returns
  /**
   * Generates the full auth context as cached in local/session storage
   *
   * @returns {AuthenticationState}
   */
  static toJSON() {
    const {
      type,
      expiresAt,
      idToken,
      accessToken,
      username,
      accountUuid,
      userUuid,
      authenticated,
      obo,
      user,
      redirectTo
    } = AuthStorage;
    return {
      type,
      expiresAt,
      idToken,
      accessToken,
      username,
      accountUuid,
      userUuid,
      authenticated,
      obo,
      user,
      redirectTo
    };
  }
}